import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Grid,
  Icon,
  Loader,
  Menu,
  Popup,
  Segment,
} from 'semantic-ui-react';
import { flashErrorMessage, flashSuccessMessage } from '../actions/flash';
import { fetchM } from '../actions/fetchM';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';

const tabs = [
  { value: 'all', name: 'all', text: 'All', header: 'ALL' },
  { value: 'booking', name: 'booking', text: 'Booking', header: 'Booking' },
  { value: 'project', name: 'project', text: 'Project', header: 'Project' },
  { value: 'unit', name: 'unit', text: 'Unit', header: 'Unit' },
  { value: 'pricing_package', name: 'pricing_package', text: 'Pricing Package', header: 'Pricing Package' },
  { value: 'purchaser', name: 'purchaser', text: 'Purchaser', header: 'Purchaser' },
  { value: 'custom', name: 'custom', text: 'Custom', header: 'Custom' },
];

class MergeTagDictTabList extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    t: PropTypes.func,
  }
  constructor(props) {
    super(props);
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;
    this.state = {
      isMobile,
      loading: true,
      mergeTags: [],
      filteredMergeTags: [],
      currentTab: tabs[0],
    };
  }
  componentDidMount() {
    this.fetchMergeTags();
    window.addEventListener('resize', this.handleResize, false);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize, false);
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  handleResize = () => {
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;
    if (isMobile !== this.state.isMobile) {
      this.setState({ isMobile });
    }
  }
  handleChangeTab = (e, { name, value }) => {
    const currentTab = tabs.find(tab => tab.name === name || tab.value === value);
    // Filter the tags based on the selected tab
    const filteredMergeTags = currentTab.value === 'all'
      ? this.state.mergeTags
      : this.state.mergeTags.filter(mergeTag => mergeTag.tag === currentTab.value);
    this.setState({
      currentTab,
      filteredMergeTags,
    });
  }

  handleCopy = async (text) => {
    const { t } = this.props;
    const { store } = this.context;
    try {
      // Copy to clipboard: currently only supported on secure contexts (i.e. HTTPS)
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea'); // Create a temporary textarea
        textArea.value = text; // Set the value to the text you want to copy
        document.body.appendChild(textArea); // Append the textarea to the document body

        textArea.select(); // Select the text inside the textarea
        textArea.setSelectionRange(0, 99999); // For mobile devices
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      await store.dispatch(flashSuccessMessage({ header: t('Success', { ns: TRANSLATION_FILES.constants }), message: t('Copied merge tag(s) to clipboard', { ns: TRANSLATION_FILES.translation }) }));
    } catch (error) {
      await store.dispatch(flashErrorMessage({ header: t('Error', { ns: TRANSLATION_FILES.constants }), message: t('Failed to copy merge tags', { ns: TRANSLATION_FILES.translation }) }));
      console.error('Failed to copy merge tags', error);
    }
  }

  async fetchMergeTags() {
    await this.setLoading(true);
    const { t } = this.props;
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM('/mergetags/', {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ header: t('Error', { ns: TRANSLATION_FILES.constants }), message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ header: t('Error', { ns: TRANSLATION_FILES.constants }), message }));
    } else {
      const mergeTags = await res.json();
      if (mergeTags && mergeTags.length > 0) {
        this.setState({
          mergeTags,
          filteredMergeTags: mergeTags,
        });
      }
    }
    await this.setLoading(false);
  }
  renderMergeTag = mergeTag => {
    const { t } = this.props;
    return (
      <div key={mergeTag.mergetag} style={{ marginBottom: '3px' }}>
        <span><strong>{t(mergeTag.title, { ns: TRANSLATION_FILES.translation })}</strong>: {mergeTag.syntax}</span>
        <Popup
          hoverable
          size="mini"
          content={t('Click to copy', { ns: TRANSLATION_FILES.translation })}
          trigger={
            <Icon
              style={{ cursor: 'pointer' }}
              name="file outline"
              color="blue"
              onClick={() => this.handleCopy(mergeTag.syntax)}
            />
          }
        />
      </div>
    );
  }
  renderCopyAllButton = () => {
    const { t } = this.props;
    const { filteredMergeTags } = this.state;
    return (
      <Button
        basic
        color="blue"
        size="small"
        content={t('Copy All', { ns: TRANSLATION_FILES.translation })}
        icon="copy outline"
        type="button"
        onClick={() => {
          const allTags = filteredMergeTags
            .map(tag => `${t(tag.title, { ns: TRANSLATION_FILES.translation })}: ${tag.syntax}`)
            .join('\n');
          this.handleCopy(allTags);
        }}
      />
    );
  }

  render() {
    const { currentTab, filteredMergeTags, isMobile, loading } = this.state;
    const { t } = this.props;
    return (
      <Grid verticalAlign="middle" className="merge-tag-tab">
        <Grid.Row className="tab-menu">
          <Grid.Column width={16}>
            { isMobile ? (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Dropdown
                    fluid
                    selection
                    options={tabs.map(tab => ({
                      key: tab.value,
                      text: tab.header,
                      value: tab.value,
                    }))}
                    value={currentTab.value}
                    onChange={(e, { value }) => this.handleChangeTab(e, { name: value })}
                    style={{ flex: 1 }}
                  />
                  {this.renderCopyAllButton()}
                </div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap' }}>
                <Menu attached="top" tabular style={{ minWidth: 'max-content', flexWrap: 'nowrap' }}>
                  {tabs.map((tab, key) => (
                    <Menu.Item
                      key={key}
                      name={tab.name}
                      onClick={this.handleChangeTab}
                      active={currentTab.value === tab.value}
                    >
                      {t(tab.header, { ns: TRANSLATION_FILES.screens })}
                    </Menu.Item>
                  ))}
                  <Menu.Menu position="right" style={{ paddingTop: '5px', paddingBottom: '5px', paddingLeft: '10px' }}>
                    {this.renderCopyAllButton()}
                  </Menu.Menu>
                </Menu>
              </div>
            )}
            <Segment
              attached={isMobile ? false : 'bottom'}
              style={{
                height: '350px',
                overflowY: 'scroll',
                overflowX: 'hidden',
                borderTop: isMobile ? '1px solid #d4d4d5' : 'none',
              }}
            >
              { loading ? (
                <Loader active inline="centered">
                  {t('Loading', { ns: TRANSLATION_FILES.translation })}
                </Loader>
              ) :
                filteredMergeTags.map(mergeTag => this.renderMergeTag(mergeTag))
              }
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation(namespaces)(MergeTagDictTabList);



// WEBPACK FOOTER //
// ./src/components/MergeTagDictTabList.jsx